import { useState } from "react";
import { ArrowLeft, CheckCircle2, Loader2, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { FieldError, errCls, isPhone } from "@/lib/validate";
import { useFormGuard } from "@/lib/formGuard";

export default function CallbackForm({ sessionId, reason, onBack }) {
  const { t, lang } = useT();
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const [errors, setErrors] = useState({});
  const { guard, Honeypot, GuardNotice } = useFormGuard();

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!phone.trim()) er.phone = t.errors.required;
    else if (!isPhone(phone)) er.phone = t.errors.phone;
    if (!consent) er.consent = t.errors.consent;
    setErrors(er);
    if (Object.keys(er).length) {
      toast.error(t.errors.summary);
      return;
    }
    setSending(true);
    try {
      await api.post("/chat/callbacks", { phone, note, session_id: sessionId || "", lang, cf: (await guard()).cf });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.status === 429 ? t.errors.too_many : t.chat.toast_err);
    } finally {
      setSending(false);
    }
  };

  if (done)
    return (
      <div data-testid="callback-success" className="flex-1 flex flex-col items-center justify-center text-center p-8">
        <CheckCircle2 size={44} className="text-slate-600 dark:text-slate-300" />
        <h4 className="font-display font-bold text-xl mt-4">{t.chat.callback_success_title}</h4>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{t.chat.callback_success_sub}</p>
        <button data-testid="callback-back-button" onClick={onBack} className="mt-6 text-xs font-semibold underline underline-offset-4 text-muted-foreground hover:text-foreground">
          {t.chat.back_to_chat}
        </button>
      </div>
    );

  return (
    <form data-testid="callback-form" onSubmit={submit} noValidate className="flex-1 flex flex-col p-5 gap-4 overflow-y-auto">
      <button type="button" data-testid="callback-back-to-chat" onClick={onBack} className="self-start inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft size={13} /> {t.chat.back_to_chat}
      </button>
      <div>
        <div className="w-10 h-10 rounded-xl bg-brand-teal text-white flex items-center justify-center">
          <PhoneCall size={17} />
        </div>
        <h4 className="font-display font-bold text-lg mt-3">{t.chat.callback_title}</h4>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{reason || t.chat.callback_sub}</p>
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{t.chat.callback_phone}</label>
        <Input data-testid="callback-phone-input" aria-invalid={!!errors.phone} type="tel" value={phone} onChange={(e) => { setPhone(e.target.value); setErrors({ ...errors, phone: undefined }); }} placeholder={t.chat.callback_phone_ph} className={`bg-background ${errCls(errors.phone)}`} />
        <FieldError id="callback-phone" msg={errors.phone} />
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{t.chat.callback_note}</label>
        <Textarea data-testid="callback-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t.chat.callback_note_ph} className="bg-background resize-none" />
      </div>
      <label className="flex items-start gap-2.5 cursor-pointer text-[11px] text-muted-foreground leading-relaxed">
        <Checkbox data-testid="callback-consent-checkbox" checked={consent} onCheckedChange={(v) => { setConsent(v); setErrors({ ...errors, consent: undefined }); }} className={`mt-0.5 ${errors.consent ? "border-rose-500" : ""}`} />
        <span>
          {t.chat.callback_consent}{" "}
          <a data-testid="callback-rodo-link" href="/biuro-wirtualne-warszawa/rodo" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="underline underline-offset-2 font-semibold text-foreground">
            {t.order.consent_link}
          </a>
        </span>
      </label>
      <FieldError id="callback-consent" msg={errors.consent} />
      <Honeypot />
      <GuardNotice />
      <button
        type="submit"
        data-testid="callback-submit-button"
        disabled={sending}
        className="mt-auto w-full bg-brand-teal text-white hover:bg-brand-tealDark disabled:opacity-60 font-semibold py-3 rounded-full text-sm transition-colors inline-flex items-center justify-center gap-2"
      >
        {sending && <Loader2 size={14} className="animate-spin" />}
        {sending ? t.chat.callback_sending : t.chat.callback_submit}
      </button>
    </form>
  );
}
